import React, { useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Typography,
    FormControl,
    FormControlLabel,
    RadioGroup,
    Radio,
    TextField,
    Box,
    Alert,
    Divider
} from '@mui/material';
import {
    Delete as DeleteIcon,
    DeleteForever as DeleteForeverIcon,
    Warning as WarningIcon
} from '@mui/icons-material';

/**
 * DeleteConfirmationDialog - Componente per confermare la cancellazione con opzione soft/hard delete
 * @param {Object} props
 * @param {boolean} props.open - Se il dialog è aperto
 * @param {Function} props.onClose - Callback per chiudere il dialog
 * @param {Function} props.onConfirm - Callback per confermare la cancellazione
 * @param {string} props.title - Titolo del dialog
 * @param {string} props.itemName - Nome dell'elemento da eliminare
 * @param {string} props.entityType - Tipo di entità (booking, diary, etc.)
 * @param {boolean} props.allowHardDelete - Se permettere hard delete (default: false)
 * @param {boolean} props.userIsAdmin - Se l'utente è admin (per hard delete)
 * @param {Object} props.relatedData - Dati collegati che verrebbero eliminati
 */
const DeleteConfirmationDialog = ({
    open,
    onClose,
    onConfirm,
    title = "Conferma Eliminazione",
    itemName,
    entityType = "elemento",
    allowHardDelete = false,
    userIsAdmin = false,
    relatedData = {}
}) => {
    const [deleteType, setDeleteType] = useState('soft');
    const [reason, setReason] = useState('');
    const [loading, setLoading] = useState(false);

    const handleConfirm = async () => {
        if (deleteType === 'hard' && !reason.trim()) {
            return; // Reason is required for hard delete
        }

        setLoading(true);
        try {
            await onConfirm({
                type: deleteType,
                reason: reason.trim(),
                isHardDelete: deleteType === 'hard'
            });
            onClose();
            setReason('');
            setDeleteType('soft');
        } catch (error) {
            console.error('Delete operation failed:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleClose = () => {
        if (!loading) {
            onClose();
            setReason('');
            setDeleteType('soft');
        }
    };

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth="md"
            fullWidth
            PaperProps={{
                sx: { minHeight: '400px' }
            }}
        >
            <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {deleteType === 'hard' ? (
                    <DeleteForeverIcon color="error" />
                ) : (
                    <DeleteIcon color="warning" />
                )}
                {title}
            </DialogTitle>

            <DialogContent>
                <Typography variant="body1" gutterBottom>
                    Sei sicuro di voler eliminare <strong>"{itemName}"</strong>?
                </Typography>

                <Box sx={{ mt: 3 }}>
                    <FormControl component="fieldset">
                        <RadioGroup
                            value={deleteType}
                            onChange={(e) => setDeleteType(e.target.value)}
                        >
                            {/* Soft Delete Option */}
                            <FormControlLabel
                                value="soft"
                                control={<Radio />}
                                label={
                                    <Box>
                                        <Typography variant="subtitle2" color="warning.main">
                                            Eliminazione Temporanea (Soft Delete)
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            L'{entityType} verrà nascosto dall'interfaccia ma i dati saranno 
                                            conservati nel database per la sincronizzazione e potranno essere 
                                            ripristinati se necessario.
                                        </Typography>
                                    </Box>
                                }
                            />

                            {/* Hard Delete Option - Only if allowed and user is admin */}
                            {allowHardDelete && userIsAdmin && (
                                <FormControlLabel
                                    value="hard"
                                    control={<Radio />}
                                    label={
                                        <Box>
                                            <Typography variant="subtitle2" color="error.main">
                                                Eliminazione Definitiva (Hard Delete)
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary">
                                                L'{entityType} verrà eliminato permanentemente dal database 
                                                locale e da RECUP. Questa operazione è <strong>IRREVERSIBILE</strong>.
                                            </Typography>
                                        </Box>
                                    }
                                />
                            )}
                        </RadioGroup>
                    </FormControl>
                </Box>

                <Divider sx={{ my: 2 }} />

                {/* Reason field */}
                <TextField
                    fullWidth
                    label={deleteType === 'hard' ? "Motivo eliminazione (obbligatorio)" : "Motivo eliminazione (opzionale)"}
                    multiline
                    rows={3}
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    required={deleteType === 'hard'}
                    error={deleteType === 'hard' && !reason.trim()}
                    helperText={
                        deleteType === 'hard' && !reason.trim() 
                            ? "Il motivo è obbligatorio per l'eliminazione definitiva"
                            : "Specifica il motivo dell'eliminazione per l'audit trail"
                    }
                    sx={{ mt: 2 }}
                />

                {/* Warning for hard delete */}
                {deleteType === 'hard' && (
                    <Alert severity="error" sx={{ mt: 2 }} icon={<WarningIcon />}>
                        <Typography variant="subtitle2" gutterBottom>
                            ⚠️ ATTENZIONE: ELIMINAZIONE DEFINITIVA
                        </Typography>
                        <Typography variant="body2">
                            • L'{entityType} sarà eliminato permanentemente dal database<br/>
                            • I dati verranno rimossi anche da RECUP se sincronizzati<br/>
                            • Questa operazione NON può essere annullata<br/>
                            {relatedData.bookings > 0 && (
                                <>• Sono presenti {relatedData.bookings} prenotazioni collegate<br/></>
                            )}
                            • Sarà registrata una traccia audit dell'operazione
                        </Typography>
                    </Alert>
                )}

                {/* Info for soft delete */}
                {deleteType === 'soft' && (
                    <Alert severity="info" sx={{ mt: 2 }}>
                        <Typography variant="body2">
                            L'{entityType} sarà nascosto dall'interfaccia ma potrà essere ripristinato 
                            dalla sezione "Elementi Eliminati" dagli amministratori.
                        </Typography>
                    </Alert>
                )}

                {/* Admin required notice */}
                {allowHardDelete && !userIsAdmin && (
                    <Alert severity="warning" sx={{ mt: 2 }}>
                        <Typography variant="body2">
                            L'eliminazione definitiva è disponibile solo per gli amministratori. 
                            È possibile solo l'eliminazione temporanea.
                        </Typography>
                    </Alert>
                )}
            </DialogContent>

            <DialogActions sx={{ p: 3, pt: 1 }}>
                <Button 
                    onClick={handleClose} 
                    disabled={loading}
                >
                    Annulla
                </Button>
                <Button
                    onClick={handleConfirm}
                    variant="contained"
                    color={deleteType === 'hard' ? 'error' : 'warning'}
                    disabled={loading || (deleteType === 'hard' && !reason.trim())}
                    startIcon={deleteType === 'hard' ? <DeleteForeverIcon /> : <DeleteIcon />}
                >
                    {loading ? 'Eliminazione...' : 
                     deleteType === 'hard' ? 'Elimina Definitivamente' : 'Elimina Temporaneamente'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default DeleteConfirmationDialog;